import { Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFunnelChartStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { ColorSchemes } from './ColorScheme';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import CustomColorBuilder from './CustomColorBuilder';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const FunnelColorControls = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
    };
  });
  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const currentControls = chartControls.properties[propKey].funnelChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;

  const chartData = chartControls.properties[propKey].chartData || [];

  const measureNames = chartData.length > 0 ? Object.keys(chartData[0]) : [];

  const updateStyleOptions = (propKey: string, option: string, value: any) => {
    dispatch(updateFunnelChartStyleOptions(propKey, option, value));
  };

  const measureSignature = measureNames.join('||');

  useEffect(() => {
    const currentMap = nodeValuesColors || {};
    const currentSavedKeys = Object.keys(currentMap);
    const keysToRemove = currentSavedKeys.filter((key) => !measureNames.includes(key));

    if (keysToRemove.length > 0) {
      const newMap = { ...currentMap };
      keysToRemove.forEach((key) => delete newMap[key]);
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  }, [measureSignature, propKey, dispatch]);

  //   Tracking the last used  Scheme ---
  const currentSchemeName = chartControls.properties[propKey].colorScheme;
  const lastManualSchemeRef = useRef(currentSchemeName);

  useEffect(() => {
    if (manualColor) {
      lastManualSchemeRef.current = currentSchemeName;
    }
  }, [manualColor, currentSchemeName]);

  const manualKeys = Object.keys(nodeValuesColors || {});
  const customList = manualKeys.filter((key) => !measureNames.includes(key) && key !== 'null');

  const allThemes = useCombinedColorSchemes();
  const selectedScheme = allThemes.find((scheme) => scheme.name === currentSchemeName);
  const themeColors = selectedScheme?.colors || ColorSchemes[0].colors;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  //  Reset Button Logic ---
  const hasIndividualChanges = Object.keys(nodeValuesColors || {}).length > 0;
  const hasGlobalChange = !!allSeriesColor;
  const showReset = hasGlobalChange || hasIndividualChanges;

  const handleColorChange = (color: string) => {
    if (selectedSeriesName === 'ALL_SERIES_COLOR') {
      updateStyleOptions(propKey, 'allSeriesColor', color);
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    } else {
      const currentMap = nodeValuesColors || {};
      const newMap = { ...currentMap, [selectedSeriesName]: color };
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  };

  const handleAddCustomColor = (name: string, color: string) => {
    const currentMap = nodeValuesColors || {};
    const newMap = { ...currentMap, [name]: color };
    updateStyleOptions(propKey, 'nodeValuesColors', newMap);
  };

  const handleRemoveColorRule = (name: string) => {
    const currentMap = { ...(nodeValuesColors || {}) };
    delete currentMap[name];
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleRemoveAllCustomColors = () => {
    const currentMap = { ...(nodeValuesColors || {}) };
    customList.forEach((key) => {
      delete currentMap[key];
    });
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  //   Resets everything ---
  const handleFullReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateStyleOptions(propKey, 'allSeriesColor', '');
    updateStyleOptions(propKey, 'nodeValuesColors', {});
  };

  const handleOpenPicker = (e: React.MouseEvent<HTMLElement>, name: string, color: string) => {
    setSelectedSeriesName(name);
    setSelectedColor(color);
    setAnchorEl(e.currentTarget);
  };

  const renderAllControl = () => {
    const displayColor = allSeriesColor || '#ffffff';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          marginBottom: 12,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
            backgroundImage: !allSeriesColor
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
              : 'none',
            backgroundSize: !allSeriesColor ? '10px 10px' : 'auto',
            backgroundPosition: !allSeriesColor ? '0 0, 0 5px, 5px -5px, -5px 0px' : '0 0',
          }}
          onClick={(e) => handleOpenPicker(e, 'ALL_SERIES_COLOR', allSeriesColor || '#000000')}
        />

        <label
          style={{
            fontSize: '13px',
            fontWeight: 600,
            marginLeft: '5px',
            flexGrow: 1,
            cursor: 'default',
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          All
        </label>

        {showReset && (
          <IconButton
            size="small"
            onClick={handleFullReset}
            sx={{
              padding: '2px',
              marginLeft: '5px',
              color: iconColor || 'lightgray',
              '&:hover': {
                color: iconColor || 'gray',
                backgroundColor: 'transparent',
              },
            }}
            title="Reset All Manual Changes"
          >
            <RestartAltIcon style={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderColorRow = (itemName: string) => {
    const manualMap = nodeValuesColors || {};
    const dataIndex = measureNames.indexOf(itemName);

    //  default base color
    let defaultBaseColor = '#cccccc';
    if (dataIndex >= 0) {
      if (allSeriesColor) {
        defaultBaseColor = allSeriesColor;
      } else {
        defaultBaseColor = themeColors[dataIndex % themeColors.length];
      }
    }

    const displayColor = manualMap[itemName] || defaultBaseColor;
    const labelText = itemName;

    return (
      <div
        key={itemName}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
          }}
          onClick={(e) => handleOpenPicker(e, itemName, displayColor)}
        />
        <label
          style={{
            fontSize: '12px',
            marginLeft: '5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'default',
            flexGrow: 1,
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
          title={labelText}
        >
          {labelText}
        </label>
      </div>
    );
  };

  const renderColorControls = () => {
    return (
      <div
        style={{ marginBottom: '10px', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px' }}
      >
        <div
          className="optionDescription"
          style={{ marginTop: '0', display: 'flex', justifyContent: 'space-around' }}
        >
          <label
            style={{
              width: '100%',
              fontWeight: 600,
              fontSize: '13px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Measures
          </label>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ padding: 2 }}
            title={isExpanded ? 'Collapse List' : 'Expand List'}
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </div>

        <Collapse in={isExpanded}>
          <div
            style={{
              marginTop: '5px',
              paddingLeft: '10px',
              paddingBottom: '10px',
            }}
          >
            {renderAllControl()}
            {measureNames.map((itemName) => renderColorRow(itemName))}
          </div>
        </Collapse>

        <CustomColorBuilder
          onAdd={handleAddCustomColor}
          existingColors={nodeValuesColors || {}}
          onRemove={handleRemoveColorRule}
          onRemoveAll={handleRemoveAllCustomColors}
          customList={customList}
          colorMap={nodeValuesColors || {}}
          colorPalette={themeColors}
          onColorChange={handleAddCustomColor}
          iconColor={iconColor}
        />
      </div>
    );
  };

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          const isSwitchingToManual = key === 'manual';

          if (isSwitchingToManual) {
            //  if scheme changed since we were last in manual
            if (currentSchemeName !== lastManualSchemeRef.current) {
              // Scheme changed! Wipe old values
              updateStyleOptions(propKey, 'allSeriesColor', '');
              updateStyleOptions(propKey, 'nodeValuesColors', {});
            }
            updateStyleOptions(propKey, 'manualColor', true);
          } else {
            //   Just switching mode.
            updateStyleOptions(propKey, 'manualColor', false);
          }
        }}
        style={{ width: '94%' }}
      />

      <div>
        {manualColor ? (
          <>
            <div style={{ marginBottom: '10px', paddingRight: '10px' }}>
              {renderColorControls()}
            </div>

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu=""
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FunnelColorControls;
